import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { hasAccessByPermission, hasAccessByRole } from '@app/controllers/accessController';
import { setHeader } from '@app/store/slices/headerSlice';
import { useEffect, useMemo, useState } from 'react';
import { BILinkModel } from '@app/domain/interfaces/bi/BILinkModel';
import { BIService } from '@app/services/biService';
import { Modal, Input, Button, Select, message } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import * as S from './styles';

const BussinessIntelligenceReport: React.FC = () => {
  const dispatch = useDispatch();

  const [biLinks, setBiLinks] = useState<BILinkModel[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [biUrl, setBiUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [expired, setExpired] = useState<boolean>(false);
  const [showLink, setShowLink] = useState<boolean>(false);

  const canEdit = useMemo(
    () =>
      hasAccessByPermission('bussiness_intelligence', 'U') || hasAccessByPermission('bussiness_intelligence_adm', 'U'),
    [],
  );

  useEffect(() => {
    dispatch(
      setHeader({
        title: 'Relatório de Business Intelligence',
      }),
    );
  }, [dispatch]);

  const loadPanels = async () => {
    setLoading(true);
    try {
      const list: BILinkModel[] = await new BIService().getArray('');

      setBiLinks(list ?? []);

      const firstId = list?.[0]?.id ?? null;
      setSelectedId((prev) => (prev != null && list.some((x) => x.id === prev) ? prev : firstId));
    } catch {
      setBiLinks([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPanels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPanel = useMemo(() => biLinks.find((x) => x.id === selectedId) ?? null, [biLinks, selectedId]);

  useEffect(() => {
    if (!selectedPanel) {
      setBiUrl('');
      setExpired(false);
      return;
    }

    setBiUrl(selectedPanel.url ?? '');
    setExpired(!!selectedPanel.expired);
  }, [selectedPanel]);

  if (!hasAccessByRole('bussiness_intelligence') && !hasAccessByRole('bussiness_intelligence_adm')) {
    return <Navigate to="/403" replace />;
  }

  const handleSaveUrl = async () => {
    if (!canEdit) return;

    if (!selectedId) {
      message.warning('Selecione um painel.');
      return;
    }

    if (!biUrl?.trim()) {
      message.warning('Cole um link válido.');
      return;
    }

    setLoading(true);
    try {
      await new BIService().updateLink(selectedId, biUrl);

      message.success('Link atualizado!');
      await loadPanels();
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.Container>
      {expired && (
        <Modal open footer={null} centered onCancel={() => setExpired(false)}>
          <div style={{ textAlign: 'center' }}>
            <h2>Link expirado</h2>
            <p>O link do painel selecionado está expirado.</p>
          </div>
        </Modal>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <span>Painel:</span>
        <Select
          style={{ minWidth: 220 }}
          loading={loading}
          value={selectedId ?? undefined}
          placeholder="Selecione um painel"
          onChange={(value) => setSelectedId(value)}
          options={biLinks.map((x) => ({
            value: x.id,
            label: x.name ?? `Painel ${x.id}`,
          }))}
        />
        {canEdit && (
          <>
            <Input
              value={biUrl}
              onChange={(e) => setBiUrl(e.target.value)}
              placeholder="Cole o link do BI aqui"
              disabled={loading || !selectedId}
              style={{ width: 320 }}
              type={showLink ? 'text' : 'password'}
              suffix={
                <Button
                  type="text"
                  onClick={() => setShowLink((v) => !v)}
                  icon={showLink ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                />
              }
            />
            <Button type="primary" onClick={handleSaveUrl} loading={loading} disabled={!selectedId}>
              Salvar
            </Button>
          </>
        )}
      </div>

      <S.IframeContainer>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            Carregando...
          </div>
        ) : biUrl ? (
          <S.StyledIframe title="slumpmix" src={biUrl} allowFullScreen />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <strong>Nenhum link configurado</strong>
            <span>Selecione um painel ou atualize o link (usuário com permissão U).</span>
          </div>
        )}
      </S.IframeContainer>
    </S.Container>
  );
};

export default BussinessIntelligenceReport;
